import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * 🆕 [Phase 43 #6] Hook réutilisable d'auto-save avec debounce.
 *
 * Usage :
 *   const { status, lastSavedAt, triggerSave } = useAutoSave(
 *     formData,                        // data à surveiller (sérialisable)
 *     async (data) => { ... },         // saveFunction async, reçoit data
 *     { debounceMs: 2000, enabled: true }
 *   );
 *
 * `status` ∈ { 'idle', 'pending', 'saving', 'saved', 'error' }
 *
 * Le hook ne modifie PAS la navigation : c'est à la saveFunction de décider
 * (ici on n'appelle pas de redirect). Le bouton « Enregistrer » manuel
 * reste indépendant de ce hook.
 *
 * Échec silencieux : si saveFunction throw, on passe status='error' mais
 * pas de crash UI.
 */
export function useAutoSave(data, saveFunction, options = {}) {
  const { debounceMs = 2000, enabled = true } = options;
  const [status, setStatus] = useState('idle');
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const timerRef = useRef(null);
  const saveRef = useRef(saveFunction);
  const lastSerializedRef = useRef(null);
  const isFirstRender = useRef(true);

  // Garder la référence à jour de saveFunction sans relancer le hook
  useEffect(() => {
    saveRef.current = saveFunction;
  }, [saveFunction]);

  // Trigger debounced quand data change
  useEffect(() => {
    if (!enabled) return undefined;
    // Skip le premier render (data initial chargé depuis serveur)
    if (isFirstRender.current) {
      isFirstRender.current = false;
      lastSerializedRef.current = JSON.stringify(data);
      return undefined;
    }
    // Skip si données identiques au dernier save (évite save inutile)
    const serialized = JSON.stringify(data);
    if (serialized === lastSerializedRef.current) return undefined;

    setStatus('pending');
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setStatus('saving');
      try {
        await saveRef.current(data);
        lastSerializedRef.current = serialized;
        setLastSavedAt(new Date());
        setStatus('saved');
      } catch (e) {
        // Échec silencieux — pas de crash UI
        console.warn('[useAutoSave] échec save', e);
        setStatus('error');
      }
    }, debounceMs);

    return () => clearTimeout(timerRef.current);
  }, [data, enabled, debounceMs]);

  // Cleanup unmount
  useEffect(() => () => clearTimeout(timerRef.current), []);

  // Trigger manuel forcé (bypass debounce)
  const triggerSave = useCallback(async () => {
    clearTimeout(timerRef.current);
    setStatus('saving');
    try {
      await saveRef.current(data);
      lastSerializedRef.current = JSON.stringify(data);
      setLastSavedAt(new Date());
      setStatus('saved');
      return true;
    } catch (e) {
      console.warn('[useAutoSave] trigger save échec', e);
      setStatus('error');
      return false;
    }
  }, [data]);

  return { status, lastSavedAt, triggerSave };
}

export default useAutoSave;
